import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  useConfigureProjectTools,
  useRetrieveProjectToolsConfiguration,
} from "@planzone/view-models";
import { Loader } from "@common/loader";
import { GanttIcon } from "./ganttIcon";
import { KanbanIcon } from "./kanbanIcon";
import { SynthesisIcon } from "./synthesisIcon";
import { TimeSheetIcon } from "./timeSheetIcon";
import { DiscussionsIcon } from "./discussionsIcon";
import { DocumentsIcon } from "./documentsIcon";
import { BudgetIcon } from "./budgetIcon";
import { CalendarIcon } from "./calendarIcon";
import { ListIcon } from "./listIcon";
import { ReportsIcon } from "./reportsIcon";
import { ToggleInput } from "./toggleInput";
import { Props } from "./types";
import styles from "./styles.scss";

function ProjectToolsConfiguration({ project }: Props) {
  const { t } = useTranslation();
  const {
    config,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    retrieveProjectToolsConfiguration,
  } = useRetrieveProjectToolsConfiguration(project);
  const {
    isRequestPending: isConfigurationRequestPending,
    configureProjectTools,
  } = useConfigureProjectTools();
  const parameters = [
    {
      icon: SynthesisIcon,
      key: "isSynthesisEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.synthesis.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.synthesis.description",
      ),
    },
    {
      icon: ListIcon,
      key: "isListsListEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.listsList.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.listsList.description",
      ),
    },
    {
      icon: GanttIcon,
      key: "isGanttEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.gantt.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.gantt.description",
      ),
    },
    {
      icon: CalendarIcon,
      key: "isCalendarEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.calendar.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.calendar.description",
      ),
    },
    {
      icon: KanbanIcon,
      key: "isBoardEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.kanban.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.kanban.description",
      ),
    },
    {
      icon: TimeSheetIcon,
      key: "areTimeSheetsEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.timeSheets.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.timeSheets.description",
      ),
    },
    {
      icon: DiscussionsIcon,
      key: "areDiscussionsEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.discussions.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.discussions.description",
      ),
    },
    {
      icon: DocumentsIcon,
      key: "areDocumentsEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.documents.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.documents.description",
      ),
    },
    {
      icon: BudgetIcon,
      key: "isBudgetEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.budget.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.budget.description",
      ),
    },
    {
      icon: ReportsIcon,
      key: "areReportsEnabled",
      name: t("projectDetails.toolsConfiguration.parameters.reports.name"),
      description: t(
        "projectDetails.toolsConfiguration.parameters.reports.description",
      ),
    },
  ];
  const isEverythingChecked = () =>
    Object.values(config).every((isEnabled) => isEnabled);

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveProjectToolsConfiguration(project);
  }, [project]);

  return (
    <div className={styles.projectToolsConfiguration}>
      {isRequestPending ? (
        <div className={styles.loader}>
          <div className={styles.loaderMain}>
            <Loader />
          </div>
        </div>
      ) : (
        <React.Fragment>
          <div className={styles.globalSettings}>
            <h3 className={styles.title}>
              {t("projectDetails.toolsConfiguration.title")}
            </h3>
            <div className={styles.input}>
              <p className={styles.label}>
                {t("projectDetails.toolsConfiguration.label")}
              </p>
              <div className={styles.toggle}>
                <ToggleInput
                  isToggled={isEverythingChecked()}
                  toggle={() =>
                    configureProjectTools({
                      project,
                      config,
                      nextConfig: Object.keys(config).reduce(
                        (nextConfig, key) => ({
                          ...nextConfig,
                          [key]: !isEverythingChecked(),
                        }),
                        config,
                      ),
                    })
                  }
                />
              </div>
            </div>
          </div>
          <div className={styles.parameters}>
            {parameters.map(({ icon: Icon, ...parameter }) => (
              <div
                className={classNames(styles.parameter, {
                  [styles.timeSheets]: parameter.key === "areTimeSheetsEnabled",
                })}
              >
                <div className={styles.iconBox}>
                  <Icon className={styles.icon} />
                </div>
                <p className={styles.name}>{parameter.name}</p>
                <p className={styles.description}>{parameter.description}</p>
                <div className={styles.toggle}>
                  <ToggleInput
                    isToggled={config[parameter.key as keyof typeof config]}
                    toggle={() =>
                      configureProjectTools({
                        project,
                        config,
                        nextConfig: {
                          ...config,
                          [parameter.key]:
                            !config[parameter.key as keyof typeof config],
                        },
                      })
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

export { ProjectToolsConfiguration };
